import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ENV_VARS } from "@/constants/env";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type {
  IPublicNfcFormPayload,
  IPublicNfcResolvePayload,
  IPublicSubmitPayload,
} from "@/types/(company)/nfc-forms.interface";

export const publicNfcFormsApi = createApi({
  reducerPath: "publicNfcFormsApi",
  baseQuery: fetchBaseQuery({ baseUrl: ENV_VARS.API_INSTANCE }),
  endpoints: (builder) => ({
    resolveNfcChip: builder.query<
      IApiResponse<IPublicNfcResolvePayload>,
      { token: string }
    >({
      query: ({ token }) => ({
        url: APIEndpoints.PublicNfc.Resolve(token),
      }),
    }),
    // One form of a tag that offers several — fetched after the visitor picks a
    // tile, so the resolve payload doesn't have to carry every form's fields.
    getPublicNfcForm: builder.query<
      IApiResponse<IPublicNfcFormPayload>,
      { token: string; formId: number }
    >({
      query: ({ token, formId }) => ({
        url: APIEndpoints.PublicNfc.Form(token, formId),
      }),
    }),
    uploadPublicNfcFile: builder.mutation<
      IApiResponse<{ fileId: string; originalName: string }>,
      { token: string; fieldId: number | string; file: File; formId?: number }
    >({
      query: ({ token, fieldId, file, formId }) => {
        const formData = new FormData();
        formData.append("file", file);
        const base = APIEndpoints.PublicNfc.UploadFile(token, fieldId);
        return {
          url: formId != null ? `${base}?formId=${formId}` : base,
          method: "POST",
          body: formData,
        };
      },
    }),
    submitPublicNfcForm: builder.mutation<
      IApiResponse<{ submissionId: number; successMessage: string | null }>,
      { token: string; payload: IPublicSubmitPayload }
    >({
      query: ({ token, payload }) => ({
        url: APIEndpoints.PublicNfc.Submit(token),
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useResolveNfcChipQuery,
  useGetPublicNfcFormQuery,
  useUploadPublicNfcFileMutation,
  useSubmitPublicNfcFormMutation,
} = publicNfcFormsApi;
